// @ts-nocheck
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  Ticket, Template, EmailChannel, Toast, ToastType,
  Customer, AppContextType, Automation, OrgSettings, ApiToken, Webhook,
} from '../schema/types';
import { appStoreRef } from './AppStoreRef';
import { ticketsService } from '../services/tickets/tickets.service';
import { commentsService } from '../services/comments/comments.service';
import { agentsService } from '../services/agents/agents.service';
import { contactsService } from '../services/contacts/contacts.service';
import { emailChannelService } from '../services/org/email-channel.service';
import { integrationsService, IntegrationsStatus } from '../services/org/integrations.service';
import { apiTokensService } from '../services/api-tokens/api-tokens.service';
import { webhooksService } from '../services/webhooks/webhooks.service';
import { spacesService } from '../services/spaces/spaces.service';
import { ApiSpace, SpaceRole } from '../schema/spaces/space.schema';
import { authService } from '../services/auth/auth.service';
import { apiClient } from '../services/api-client';
import { EMAIL_TEMPLATES, API_BASE, AUTOMATIONS, ORG_SETTINGS, TICKETS } from '../api-endpoints/api-endpoints';
import { mapApiTicket, mapApiComment, extractCustomers } from './dataMapper';
import { linkTicketCustomers, mergeContacts, buildTicketPayload } from '../lib/customerLink';
import { isAssignmentOnlyUser } from '../lib/assignmentOnly';
import { showToast } from '../components/Toast/Toast_Component';

// ─── Navigation (HTML5 history routing — clean URLs, no #) ───────
// pushState doesn't emit an event, so navigate() fires a custom event that the
// store + shell subscribe to (alongside the browser's native `popstate` for
// back/forward).
export const LOCATION_EVENT = 'sk:locationchange';

/** Current in-app path: pathname + query, defaulting to /tickets at the root. */
export function currentPath(): string {
  if (typeof window === 'undefined') return '/tickets';
  const p = (window.location.pathname || '/') + (window.location.search || '');
  return p && p !== '/' ? p : '/tickets';
}

export function navigate(p: string) {
  if (typeof window === 'undefined') return;
  if (currentPath() === p) return;
  window.history.pushState(null, '', p);
  window.dispatchEvent(new Event(LOCATION_EVENT));
}

export function matchRoute(fullPath: string): { name: string; params?: Record<string, string> } {
  // Split off any ?query string. Path segments drive the route name; the query
  // carries view state (e.g. the inbox tab + search term) so it survives refresh
  // and is shareable.
  const qIdx = fullPath.indexOf('?');
  const path = qIdx >= 0 ? fullPath.slice(0, qIdx) : fullPath;
  const queryStr = qIdx >= 0 ? fullPath.slice(qIdx + 1) : '';
  const parseQuery = (qs: string): Record<string, string> => {
    const out: Record<string, string> = {};
    if (!qs) return out;
    qs.split('&').forEach((kv) => {
      if (!kv) return;
      const eq = kv.indexOf('=');
      const k = eq >= 0 ? kv.slice(0, eq) : kv;
      const v = eq >= 0 ? kv.slice(eq + 1) : '';
      if (k) out[decodeURIComponent(k)] = decodeURIComponent(v);
    });
    return out;
  };

  if (!path || path === '/' || path === '/tickets') return { name: 'tickets-list', params: { view: 'all' } };
  const mv = path.match(/^\/tickets\/view\/(\w+)$/);
  if (mv) return { name: 'tickets-list', params: { view: mv[1] } };
  const m1 = path.match(/^\/tickets\/(TKT-\w+)$/);
  if (m1) return { name: 'ticket-detail', params: { id: m1[1] } };
  if (path === '/inbox') return { name: 'inbox', params: parseQuery(queryStr) };
  if (path === '/customers') return { name: 'customers' };
  const mc = path.match(/^\/customers\/(.+)$/);
  if (mc) return { name: 'customer-profile', params: { email: decodeURIComponent(mc[1]) } };
  if (path === '/reports') return { name: 'reports' };
  if (path === '/admin/settings') return { name: 'admin-home' };
  if (path === '/admin/settings/email-channel') return { name: 'email-channel' };
  if (path === '/admin/settings/email-templates') return { name: 'email-templates' };
  const m2 = path.match(/^\/admin\/settings\/email-templates\/([\w-]+)$/);
  if (m2) return { name: 'template-editor', params: { slug: m2[1] } };
  if (path === '/admin/settings/general') return { name: 'admin-general' };
  if (path === '/admin/settings/agents') return { name: 'admin-agents' };
  if (path === '/admin/settings/tags') return { name: 'admin-tags' };
  if (path === '/admin/settings/notifications') return { name: 'admin-notifications' };
  if (path === '/admin/settings/api') return { name: 'admin-api' };
  if (path === '/admin/settings/automations') return { name: 'admin-automations' };
  // INTERNAL space partition admin (must precede the generic stub match below).
  if (path === '/admin/settings/spaces') return { name: 'admin-spaces' };
  const msp = path.match(/^\/admin\/settings\/spaces\/([\w-]+)$/);
  if (msp) return { name: 'space-detail', params: { id: msp[1] } };
  const ma = path.match(/^\/admin\/settings\/(\w[\w-]*)$/);
  if (ma) return { name: 'stub', params: { kind: 'admin-' + ma[1] } };
  return { name: 'tickets-list', params: { view: 'all' } };
}

// ─── Helpers ──────────────────────────────────────────────────
const CONTACT_COLORS = [
  'linear-gradient(135deg,#0ea5e9,#6366f1)', 'linear-gradient(135deg,#f97316,#db2777)',
  'linear-gradient(135deg,#10b981,#0ea5e9)', 'linear-gradient(135deg,#a855f7,#ec4899)',
  'linear-gradient(135deg,#06b6d4,#3b82f6)', 'linear-gradient(135deg,#84cc16,#22c55e)',
];
const colorForKey = (s: string) => {
  let h = 0; for (let i = 0; i < (s || '').length; i++) h = (h * 31 + s.charCodeAt(i)) & 0xffffffff;
  return CONTACT_COLORS[Math.abs(h) % CONTACT_COLORS.length];
};
// Map a backend contact (caller) row → the shape the Contacts screen renders.
const mapApiContact = (c: any) => {
  const name = c.displayName || c.email || c.phone || 'Contact';
  const initials = name.trim().split(/\s+/).slice(0, 2).map((w: string) => w[0] || '').join('').toUpperCase() || '?';
  return {
    _contactId: c.id,
    name, email: c.email || '', phone: c.phone || '',
    org: c.company || '', initials, color: colorForKey(c.email || c.id || name),
    tags: [], since: c.createdAt ? new Date(c.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '',
    createdAt: c.createdAt || null,
  };
};

const newId = () => (Date.now().toString(36) + Math.random().toString(36).slice(2, 6));
const nowTime = () => {
  const d = new Date();
  let h = d.getHours(); const m = d.getMinutes(); const ap = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${m.toString().padStart(2, '0')} ${ap}`;
};

// ─── Context ──────────────────────────────────────────────────
const AppCtx = createContext<AppContextType | null>(null);

export function useApp(): AppContextType {
  const ctx = useContext(AppCtx);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}

// ─── Email Channel default ────────────────────────────────────
const DEFAULT_CHANNEL: EmailChannel = {
  enabled: true, senderName: 'Support', senderEmail: '',
  verification: 'unverified', dnsOpen: false, dnsVerified: false,
  inboundAddress: '', csatDelayHours: 24, tagRouting: true,
};

// ─── Provider ─────────────────────────────────────────────────
// `autoLoad` (default true) controls whether the initial authenticated data
// fetch runs on mount. The web app mounts this provider app-wide (so the
// toast host + appStoreRef bridge exist on the login/signup screens too) and
// passes autoLoad={isAuthenticated} — so no authenticated API calls fire
// until the user is logged in. Native keeps the default (true).
export function AppProvider({ children, autoLoad = true, currentUser = null, isAdmin = true }: { children: React.ReactNode; autoLoad?: boolean; currentUser?: { id: string; first_name?: string; last_name?: string; email?: string } | null; isAdmin?: boolean }) {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [channel, setChannel] = useState<EmailChannel>(DEFAULT_CHANNEL);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [contacts, setContacts] = useState<Customer[]>([]);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [agentMap, setAgentMap] = useState<Record<string, string>>({}); // platformUserId → displayName
  const [automations, setAutomations] = useState<Automation[]>([]);
  const [orgSettings, setOrgSettings] = useState<OrgSettings | null>(null);
  const [apiTokens, setApiTokens] = useState<ApiToken[]>([]);
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [integrationsStatus, setIntegrationsStatus] = useState<IntegrationsStatus | null>(null);
  const [signingSecretEndpointId, setSigningSecretEndpointId] = useState<string | null>(null);
  // ─── INTERNAL space partition (SKTICK-INTSPACE-001) ───
  // `spaces` = accessible spaces (already ACL-filtered server-side; switcher
  // source). `allSpaces` = every space in the org (admin list, org-admin only).
  // `activeSpaceId` = the client-side UI scope, persisted per user. The server's
  // RLS is the real boundary — these only drive what the UI shows.
  const [spaces, setSpaces] = useState<ApiSpace[]>([]);
  const [allSpaces, setAllSpaces] = useState<ApiSpace[]>([]);
  const [activeSpaceId, setActiveSpaceIdState] = useState<string | null>(null);
  const activeSpaceKey = `spaces:active:${currentUser?.id ?? 'anon'}`;
  // Inbox/notification read-state — persisted locally (there is no server-side
  // read-state). Real persistence via AsyncStorage (localStorage on web).
  // Scoped per-user: a shared browser must NOT leak one user's read-state into
  // the next login, otherwise an agent's freshly-assigned ticket shows as
  // already-read (Inbox "Unread" empty).
  const [readInboxIds, setReadInboxIds] = useState<string[]>([]);
  const readInboxKey = `inbox:readIds:${currentUser?.id ?? 'anon'}`;

  useEffect(() => {
    // Reset in-memory state first so switching users never shows the previous
    // user's read items while the new key loads.
    setReadInboxIds([]);
    AsyncStorage.getItem(readInboxKey)
      .then(v => { if (v) { try { setReadInboxIds(JSON.parse(v)); } catch {} } })
      .catch(() => {});
  }, [readInboxKey]);

  const persistReadInbox = (ids: string[]) => {
    const unique = Array.from(new Set(ids));
    setReadInboxIds(unique);
    AsyncStorage.setItem(readInboxKey, JSON.stringify(unique)).catch(() => {});
  };
  // templateMeta: maps slug → { templateId, locale } for API calls
  const templateMetaRef = useRef<Record<string, { templateId: string; locale: string }>>({});

  const [route, setRoute] = useState(() => {
    if (typeof window !== 'undefined') {
      return matchRoute(currentPath());
    }
    return { name: 'tickets-list', params: { view: 'all' } };
  });

  React.useEffect(() => {
    const onChange = () => setRoute(matchRoute(currentPath()));
    window.addEventListener('popstate', onChange);
    window.addEventListener(LOCATION_EVENT, onChange);
    return () => {
      window.removeEventListener('popstate', onChange);
      window.removeEventListener(LOCATION_EVENT, onChange);
    };
  }, []);

  // ─── Wire appStoreRef ────────────────────────────────────────
  // Non-React modules (Error_Handler/Success_Handler) fire toasts through this
  // bridge; route them to the same single toast component.
  React.useEffect(() => {
    appStoreRef.addToast = (type, text1, text2) => showToast(type as any, text1, text2);
    return () => { appStoreRef.addToast = null; };
  });

  // ─── Initial data load (only when enabled, e.g. authenticated) ──
  useEffect(() => {
    if (autoLoad) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad, currentUser?.id, isAdmin]);

  async function loadAll() {
    setIsLoading(true);
    try {
      // Ensure the workspace admin/owner is a registered agent so they're
      // assignable (incl. self-assign). Idempotent + best-effort — never blocks
      // the dashboard load. Regular agents are already registered.
      if (isAdmin && currentUser?.id) {
        try { await agentsService.ensureSelf(currentUser); } catch { /* non-fatal */ }
      }

      // Regular agents are scoped to the tickets assigned to them; workspace
      // admins (org_admin) see the whole queue. This is what makes an agent's
      // dashboard/inbox show "my assigned tickets" the moment they log in.
      const ticketsQuery: Record<string, any> = { limit: 200 };
      if (!isAdmin && currentUser?.id) ticketsQuery.assignedTo = currentUser.id;
      const [ticketsRes, channelRes, templatesRes, agentsRes, orgSettingsRes, contactsRes, spacesRes, allSpacesRes] = await Promise.allSettled([
        ticketsService.list(ticketsQuery),
        emailChannelService.get(),
        apiClient.get(`${API_BASE}/email-templates`),
        agentsService.list(),
        apiClient.get(ORG_SETTINGS.get),
        contactsService.list(),
        spacesService.listAccessible(),
        // Admin management list — 403s for non-admins (allSettled swallows it).
        isAdmin ? spacesService.listAll() : Promise.resolve({ data: [], count: 0 }),
      ]);

      // ─── Spaces (partition) ───
      // Resolve the accessible spaces first so we can pick a valid active space
      // before the UI renders. The persisted choice wins if it's still
      // accessible; otherwise we fall back to the org's PUBLIC space.
      let accessibleSpacesList: ApiSpace[] = [];
      if (spacesRes.status === 'fulfilled') {
        accessibleSpacesList = (spacesRes.value as any)?.data || [];
        setSpaces(accessibleSpacesList);
      }
      if (allSpacesRes.status === 'fulfilled') {
        setAllSpaces((allSpacesRes.value as any)?.data || []);
      }
      if (accessibleSpacesList.length > 0) {
        let persisted: string | null = null;
        try { persisted = await AsyncStorage.getItem(activeSpaceKey); } catch {}
        const stillAccessible = persisted && accessibleSpacesList.some(s => s.id === persisted);
        const fallback = accessibleSpacesList.find(s => s.type === 'PUBLIC') || accessibleSpacesList[0];
        setActiveSpaceIdState(stillAccessible ? persisted : fallback.id);
      }

      // Build agent map (platformUserId → displayName)
      const aMap: Record<string, any> = {};
      const nameMap: Record<string, string> = {};
      if (agentsRes.status === 'fulfilled') {
        const agentList = agentsRes.value?.data || agentsRes.value || [];
        (Array.isArray(agentList) ? agentList : []).forEach((a: any) => {
          if (a.platformUserId) {
            aMap[a.platformUserId] = a;
            nameMap[a.platformUserId] = a.displayName || a.name || a.platformUserId;
          }
        });
      }
      // The current user (e.g. the org owner) is not necessarily a tickets
      // "agent" record, so their own replies/assignments would otherwise show a
      // raw usr_… id. Seed the map with their own name as a fallback.
      if (currentUser?.id) {
        const full = [currentUser.first_name, currentUser.last_name].filter(Boolean).join(' ').trim()
          || currentUser.email || currentUser.id;
        if (!nameMap[currentUser.id]) {
          aMap[currentUser.id] = { platformUserId: currentUser.id, displayName: full };
          nameMap[currentUser.id] = full;
        }
      }
      setAgentMap(nameMap);

      // Map tickets
      if (ticketsRes.status === 'fulfilled') {
        const rawTickets = ticketsRes.value?.data || ticketsRes.value || [];
        const mapped = (Array.isArray(rawTickets) ? rawTickets : []).map((t: any) => {
          const ticket = mapApiTicket(t);
          // Enrich assignee name from agent map
          if (ticket.assignee && t.assignedTo && aMap[t.assignedTo]) {
            const agent = aMap[t.assignedTo];
            const dn = agent.displayName || agent.name || t.assignedTo;
            ticket.assignee.name = dn;
            ticket.assignee.initials = dn.split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2);
          }
          return ticket;
        });
        // Contacts = real backend contacts (SK-CRM / callers) + any customer a
        // ticket names that is not one of them. Each ticket is first tied to its
        // contact (by requesterId, else email) — see lib/customerLink.
        const apiContacts = contactsRes.status === 'fulfilled'
          ? ((contactsRes.value as any)?.data || []).map(mapApiContact)
          : [];
        const linked = linkTicketCustomers(mapped, apiContacts);
        setTickets(linked);
        setContacts(mergeContacts(apiContacts, extractCustomers(linked)));
      }

      // Map email channel
      if (channelRes.status === 'fulfilled') {
        const ec = channelRes.value?.emailChannel || channelRes.value || {};
        setChannel({
          enabled: true,
          senderName: ec.brandName || 'Support',
          senderEmail: ec.supportFromEmail || '',
          verification: ec.supportFromVerified ? 'verified' : 'unverified',
          dnsOpen: false,
          dnsVerified: ec.supportFromVerified || false,
          inboundAddress: ec.inboundAddress || '',
          csatDelayHours: 24,
          tagRouting: true,
        });
      }

      // Map email templates
      if (templatesRes.status === 'fulfilled') {
        const apiTemplates = (templatesRes.value as any)?.data || templatesRes.value || [];
        const meta: Record<string, { templateId: string; locale: string }> = {};
        const mappedTpls: Template[] = (Array.isArray(apiTemplates) ? apiTemplates : []).map((t: any) => {
          const slug = t.templateId + '_' + (t.locale || 'en');
          meta[slug] = { templateId: t.templateId, locale: t.locale || 'en' };
          return {
            slug,
            name: (t.templateId || '').replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
            desc: `Email template: ${t.templateId} (${t.locale || 'en'})`,
            status: t.isCustomised ? 'customised' : 'default',
            editedAgo: null,
            editedBy: null,
            subject: t.subject || '',
            body: t.bodyHtml || t.body || '',
            locale: t.locale || 'en',
          };
        });
        templateMetaRef.current = meta;
        setTemplates(mappedTpls);
      }
      // Map org settings
      if (orgSettingsRes.status === 'fulfilled') {
        const s = orgSettingsRes.value || {};
        setOrgSettings(s);
      }
    } catch (e) {
      console.error('loadAll error:', e);
    } finally {
      setIsLoading(false);
    }
  }

  // ─── Automation actions ───────────────────────────────────────
  const loadAutomations = async () => {
    try {
      const res = await apiClient.get(AUTOMATIONS.list);
      const items = res?.data || res || [];
      const mapped: Automation[] = (Array.isArray(items) ? items : []).map((a: any) => ({
        id: a.id,
        name: a.name,
        enabled: a.isActive ?? false,
        trigger: a.eventType || '',
        conditions: a.conditions || [],
        actions: a.actions ? (Array.isArray(a.actions) ? a.actions : Object.entries(a.actions).map(([type, param]) => ({ type, param: String(param) }))) : [],
        runs: a.runs ?? 0,
      }));
      setAutomations(mapped);
    } catch (e) {
      console.error('loadAutomations error:', e);
    }
  };

  const toggleAutomation = async (id: string) => {
    const current = automations.find(a => a.id === id);
    if (!current) return;
    const newActive = !current.enabled;
    try {
      try {
        await apiClient.post(`${AUTOMATIONS.update(id)}/toggle`);
      } catch {
        await apiClient.patch(AUTOMATIONS.update(id), { isActive: newActive });
      }
      setAutomations(as => as.map(a => a.id === id ? { ...a, enabled: newActive } : a));
    } catch (e) {
      toast('Failed to toggle automation.', 'danger', 'x');
    }
  };

  const deleteAutomation = async (id: string) => {
    try {
      await apiClient.delete(AUTOMATIONS.delete(id));
      setAutomations(as => as.filter(a => a.id !== id));
      toast('Rule deleted.', 'info');
    } catch (e) {
      toast('Failed to delete automation.', 'danger', 'x');
    }
  };

  // Build the backend rule payload from the editor's shape. The backend
  // expects: { name, eventType, conditions: {all:[{field,op,value}]},
  // actions: {...}, isActive, priority }.
  const toRulePayload = (data: any) => {
    const conditions = Array.isArray(data.conditions) && data.conditions.length
      ? { all: data.conditions.map((c: any) => ({ field: c.field, op: c.op || 'eq', value: c.value })) }
      : (data.conditions || {});
    const actions = Array.isArray(data.actions)
      ? data.actions.reduce((acc: any, a: any) => { if (a.type) acc[a.type] = a.param; return acc; }, {})
      : (data.actions || {});
    return {
      name: data.name,
      eventType: data.trigger || data.eventType || 'ticket.created',
      conditions,
      actions,
      isActive: data.enabled ?? true,
      ...(data.priority != null ? { priority: data.priority } : {}),
    };
  };

  const createAutomation = async (data: any) => {
    try {
      await apiClient.post(AUTOMATIONS.create, toRulePayload(data));
      await loadAutomations();
      toast('Rule created.', 'success', 'check');
    } catch (e) {
      toast('Failed to create rule.', 'danger', 'x');
    }
  };

  const updateAutomation = async (id: string, patch: any) => {
    try {
      await apiClient.patch(AUTOMATIONS.update(id), toRulePayload(patch));
      await loadAutomations();
      toast('Rule updated.', 'success', 'check');
    } catch (e) {
      toast('Failed to update rule.', 'danger', 'x');
    }
  };

  // ─── Org settings actions ─────────────────────────────────────
  const saveOrgSettings = async (patch: Partial<OrgSettings>) => {
    try {
      const updated = await apiClient.patch(ORG_SETTINGS.update, patch);
      setOrgSettings(s => ({ ...(s || {}), ...patch, ...(updated || {}) }));
      toast('Settings saved.', 'success', 'check');
    } catch (e) {
      toast('Failed to save settings.', 'danger', 'x');
    }
  };

  // ─── API token actions ────────────────────────────────────────
  // Map a backend token row → the shape the ApiScreen renders. The server
  // never returns the plaintext after creation, so `token` shows the masked
  // value (e.g. sktk_a1b2…cd34) — real data, not a fake secret.
  const mapToken = (t: any): ApiToken => ({
    id: t.id,
    name: t.name,
    scope: Array.isArray(t.scopes) ? (t.scopes.includes('write') || t.scopes.includes('read+write') ? 'read+write' : (t.scopes[0] || 'read')) : 'read+write',
    token: t.masked || '',
    createdAt: t.createdAt,
    lastUsed: t.lastUsedAt || null,
  });

  const loadApiTokens = async () => {
    try {
      const rows = await apiTokensService.list();
      setApiTokens(rows.map(mapToken));
    } catch (e) {
      console.error('loadApiTokens error:', e);
    }
  };

  const createApiToken = async (data: any): Promise<any | null> => {
    try {
      // The modal sends a single `scope` string ('read' | 'read+write').
      const scopes = data.scope === 'read' ? ['read'] : ['read', 'write'];
      const created = await apiTokensService.create({ name: data.name, scopes });
      setApiTokens(ts => [mapToken(created), ...ts]);
      toast(`API token "${data.name}" generated.`, 'success', 'check');
      // Return the raw created object so the screen can show the one-time plaintext.
      return created;
    } catch (e) {
      toast('Failed to create API token.', 'danger', 'x');
      return null;
    }
  };

  const revokeApiToken = async (id: string) => {
    await apiTokensService.revoke(id);
    setApiTokens(ts => ts.filter(t => t.id !== id));
  };

  const updateApiToken = async (id: string, name: string) => {
    const updated = await apiTokensService.update(id, { name });
    setApiTokens(ts => ts.map(t => (t.id === id ? mapToken(updated) : t)));
    return updated;
  };

  // ─── Webhook actions ──────────────────────────────────────────
  const mapWebhook = (w: any): Webhook => ({
    id: w.id,
    url: w.url,
    events: w.events || [],
    status: w.active ? 'active' : 'paused',
    signingSecret: w.signingSecret,
    lastDelivery: w.lastDeliveryAt
      ? new Date(w.lastDeliveryAt).toLocaleString()
      : (w.lastStatus || 'never'),
  });

  const loadWebhooks = async () => {
    try {
      const rows = await webhooksService.list();
      setWebhooks(rows.map(mapWebhook));
    } catch (e) {
      console.error('loadWebhooks error:', e);
    }
  };

  const createWebhook = async (data: any) => {
    try {
      const wh = await webhooksService.create(data);
      setWebhooks(ws => [mapWebhook(wh), ...ws]);
      toast('Webhook added.', 'success', 'check');
    } catch (e) {
      toast('Failed to add webhook.', 'danger', 'x');
    }
  };

  const updateWebhook = async (id: string, patch: any) => {
    try {
      const wh = await webhooksService.update(id, patch);
      setWebhooks(ws => ws.map(w => w.id === id ? mapWebhook(wh) : w));
      toast('Webhook updated.', 'success', 'check');
    } catch (e) {
      toast('Failed to update webhook.', 'danger', 'x');
    }
  };

  const deleteWebhook = async (id: string) => {
    try {
      await webhooksService.remove(id);
      setWebhooks(ws => ws.filter(w => w.id !== id));
      toast('Webhook removed.', 'info');
    } catch (e) {
      toast('Failed to remove webhook.', 'danger', 'x');
    }
  };

  const testWebhook = async (id: string) => {
    try {
      await webhooksService.test(id);
      toast('Test delivery enqueued.', 'success', 'send');
    } catch (e) {
      toast('Webhook test failed.', 'danger', 'x');
    }
  };

  const loadSigningSecret = async (): Promise<string | null> => {
    try {
      const res = await webhooksService.signingSecret();
      setSigningSecretEndpointId(res?.endpointId ?? null);
      return res?.signingSecret ?? null;
    } catch (e) {
      return null;
    }
  };

  const rotateSigningSecret = async (): Promise<string | null> => {
    if (!signingSecretEndpointId) {
      toast('Add a webhook first — each endpoint has its own secret.', 'info');
      return null;
    }
    try {
      const res = await webhooksService.rotateSecret(signingSecretEndpointId);
      toast('Signing secret rotated. Update your verifier.', 'warn', 'refresh');
      await loadWebhooks();
      return res?.signingSecret ?? null;
    } catch (e) {
      toast('Failed to rotate signing secret.', 'danger', 'x');
      return null;
    }
  };

  // ─── Integrations status (honest "configured?" flags) ──────────
  const loadIntegrationsStatus = async (): Promise<IntegrationsStatus | null> => {
    try {
      const s = await integrationsService.get();
      setIntegrationsStatus(s);
      return s;
    } catch (e) {
      return null;
    }
  };

  // ─── Toast ───────────────────────────────────────────────────
  // Single toast surface (react-native-toast-message, same as sk-voice-fe).
  // `icon` is no longer used — the toast host renders its own type styling.
  const toast = (message: string, type: ToastType = 'info', _icon?: string) => {
    showToast(type as any, message);
  };

  // ─── Find ticket's API id ────────────────────────────────────
  const getApiId = (frontendId: string): string | null => {
    const t = tickets.find(t => t.id === frontendId);
    return (t as any)?._apiId || null;
  };

  // ─── Ticket actions ──────────────────────────────────────────
  const updateTicketLocal = (id: string, fn: (t: Ticket) => Ticket) => {
    setTickets(ts => ts.map(t => t.id === id ? fn(t) : t));
  };

  const postComment = async (ticketId: string, { type, body }: { type: string; body: string }) => {
    if (!body.trim()) return;
    const apiId = getApiId(ticketId);
    if (!apiId) return;
    const isInternal = type === 'internal';
    try {
      const comment = await commentsService.create(apiId, { body, isInternal });
      const mappedComment = mapApiComment(comment);
      // Resolve author display name
      if (comment.authorPlatformUserId && agentMap[comment.authorPlatformUserId]) {
        mappedComment.author = agentMap[comment.authorPlatformUserId];
      }
      updateTicketLocal(ticketId, t => ({ ...t, comments: [...t.comments, mappedComment] }));
      if (isInternal) toast('Internal note added · agents only.', 'info', 'lock');
      else toast('Reply sent.', 'success', 'check');
    } catch (e: any) {
      toast('Failed to send reply.', 'danger', 'x');
    }
  };

  // Edit an existing comment (author-only; backend enforces). Returns true on
  // success so the caller can show its success dialog.
  const editComment = async (ticketId: string, commentId: string, body: string): Promise<boolean> => {
    if (!body.trim()) return false;
    try {
      const updated = await commentsService.update(commentId, body.trim());
      updateTicketLocal(ticketId, t => ({
        ...t,
        comments: t.comments.map(c =>
          c.id === commentId ? { ...c, body: updated.body ?? body.trim(), editedAt: (updated as any).editedAt ?? new Date().toISOString() } : c,
        ),
      }));
      return true;
    } catch (e: any) {
      const msg = e?.response?.status === 403 ? 'You can only edit your own messages.' : 'Failed to edit message.';
      toast(msg, 'danger', 'x');
      return false;
    }
  };

  // Delete a comment (author-only). Returns true on success.
  const deleteComment = async (ticketId: string, commentId: string): Promise<boolean> => {
    try {
      await commentsService.remove(commentId);
      updateTicketLocal(ticketId, t => ({ ...t, comments: t.comments.filter(c => c.id !== commentId) }));
      return true;
    } catch (e: any) {
      const msg = e?.response?.status === 403 ? 'You can only delete your own messages.' : 'Failed to delete message.';
      toast(msg, 'danger', 'x');
      return false;
    }
  };

  const resolveTicket = async (id: string) => {
    const apiId = getApiId(id);
    if (!apiId) return;
    const t = tickets.find(x => x.id === id);
    const newStatus = t?.status === 'resolved' ? 'open' : 'resolved';
    const apiStatus = newStatus === 'open' ? 'reopened' : 'resolved';
    try {
      await ticketsService.updateStatus(apiId, apiStatus);
      updateTicketLocal(id, t => ({ ...t, status: newStatus as any, _apiStatus: apiStatus }));
      if (newStatus === 'resolved') toast('Ticket resolved · CSAT email in 24h.', 'success', 'check');
      else toast('Ticket re-opened.', 'info', 'refresh');
    } catch (e) {
      toast('Failed to update ticket status.', 'danger', 'x');
    }
  };

  // Soft-delete a ticket. Returns true on success so the caller (e.g. the inbox
  // delete flow) can show its success dialog only when the API actually removed
  // it. We optimistically drop the ticket from local state on success — the
  // backend stamps deleted_at and filters it from every read thereafter.
  const deleteTicket = async (id: string): Promise<boolean> => {
    const apiId = getApiId(id);
    if (!apiId) { toast('Cannot delete this ticket.', 'danger', 'x'); return false; }
    try {
      await ticketsService.remove(apiId);
      setTickets(ts => ts.filter(t => t.id !== id));
      return true;
    } catch (e) {
      toast('Failed to delete ticket.', 'danger', 'x');
      return false;
    }
  };

  const toggleDeliveryExpanded = (ticketId: string, commentId: string) => {
    updateTicketLocal(ticketId, t => ({
      ...t,
      comments: t.comments.map(c => c.id === commentId ? { ...c, _expanded: !c._expanded } : c),
    }));
  };

  // Load comments when a specific ticket is opened
  const loadTicketComments = async (ticketId: string) => {
    const apiId = getApiId(ticketId);
    if (!apiId) return;
    try {
      const res = await commentsService.list(apiId, true);
      const rawComments = res?.data || res || [];
      const mapped = (Array.isArray(rawComments) ? rawComments : []).map((c: any) => {
        const mc = mapApiComment(c);
        // Resolve agent display name from agentMap
        if (c.authorPlatformUserId && agentMap[c.authorPlatformUserId]) {
          mc.author = agentMap[c.authorPlatformUserId];
        }
        return mc;
      });
      updateTicketLocal(ticketId, t => ({ ...t, comments: mapped }));
    } catch (e) {
      console.error('Failed to load comments:', e);
    }
  };

  // Re-fetch a single ticket from the API and merge its workflow fields
  // (status / priority / assignee / tags / category …) into the store, plus
  // refresh its comment thread. This is what the detail screen calls after a
  // status / priority / assign change so the UI reflects the server state —
  // loadTicketComments alone only refreshes comments, leaving those fields stale.
  const reloadTicket = async (ticketId: string) => {
    const apiId = getApiId(ticketId);
    if (!apiId) return;
    try {
      const [fresh, commentsRes] = await Promise.all([
        ticketsService.get(apiId),
        commentsService.list(apiId, true).catch(() => null),
      ]);
      const rawComments = (commentsRes as any)?.data || commentsRes || [];
      const mappedComments = (Array.isArray(rawComments) ? rawComments : []).map((c: any) => {
        const mc = mapApiComment(c);
        if (c.authorPlatformUserId && agentMap[c.authorPlatformUserId]) {
          mc.author = agentMap[c.authorPlatformUserId];
        }
        return mc;
      });
      const mapped = mapApiTicket(fresh);
      // Resolve assignee display name from agentMap when available.
      let assignee = mapped.assignee;
      if ((fresh as any).assignedTo && agentMap[(fresh as any).assignedTo]) {
        const name = agentMap[(fresh as any).assignedTo];
        assignee = { name, initials: name.slice(0, 2).toUpperCase(), color: assignee?.color } as any;
      }
      updateTicketLocal(ticketId, t => ({
        ...t,
        status: mapped.status,
        _apiStatus: (fresh as any).status,
        priority: mapped.priority,
        category: mapped.category,
        assignee,
        assignedTo: (fresh as any).assignedTo ?? null,
        tags: mapped.tags,
        subject: mapped.subject,
        replyTo: mapped.replyTo,
        slaMinutes: mapped.slaMinutes,
        comments: mappedComments,
      }));
    } catch (e) {
      console.error('Failed to reload ticket:', e);
    }
  };

  const addTicket = async (data: any) => {
    try {
      // A picked contact goes as the requester (requesterId + phone), so the
      // ticket belongs to it — see lib/customerLink.buildTicketPayload.
      const payload = buildTicketPayload(data);
      const created = await ticketsService.create(payload);
      const [mapped] = linkTicketCustomers([mapApiTicket(created)], contacts);
      setTickets(ts => [mapped, ...ts]);
      toast(`Ticket ${mapped.id} created.`, 'success', 'check');
      setTimeout(() => navigate('/tickets/' + mapped.id), 100);
    } catch (e: any) {
      // Surface the backend's reason (e.g. a field validation error) so the
      // user sees *why* it failed instead of a blanket message.
      const data = e?.response?.data;
      const fieldErr = data?.issues?.fieldErrors
        ? Object.entries(data.issues.fieldErrors)
            .map(([f, msgs]: [string, any]) => `${f}: ${(msgs ?? []).join(', ')}`)
            .join(' · ')
        : '';
      toast(fieldErr || data?.message || 'Failed to create ticket.', 'danger', 'x');
    }
  };

  // ─── Email channel actions ────────────────────────────────────
  const updateChannel = (patch: Partial<EmailChannel>) => setChannel(c => ({ ...c, ...patch }));

  const saveChannel = async () => {
    try {
      const payload: any = { brandName: channel.senderName };
      if (channel.senderEmail) payload.supportFromEmail = channel.senderEmail;
      await emailChannelService.update(payload);
      toast('Email channel settings saved.', 'success', 'check');
    } catch {
      toast('Failed to save channel settings.', 'danger', 'x');
    }
  };

  const sendVerification = async () => {
    try {
      if (channel.senderEmail) {
        await emailChannelService.update({ supportFromEmail: channel.senderEmail });
      }
      await emailChannelService.verifySender();
      updateChannel({ verification: 'pending' });
      toast(`Verification email sent to ${channel.senderEmail}.`, 'info', 'mail');
    } catch {
      toast('Set a sender email first.', 'warn', 'alert');
    }
  };

  const confirmVerification = async () => {
    // Verification really happens server-side when the admin clicks the link
    // in the confirmation email. Re-fetch the channel to reflect the true
    // supportFromVerified state instead of optimistically asserting it.
    try {
      const res: any = await emailChannelService.get();
      const ec = res?.emailChannel || res || {};
      const verified = !!ec.supportFromVerified;
      updateChannel({ verification: verified ? 'verified' : 'pending', dnsVerified: verified });
      toast(
        verified
          ? `${channel.senderEmail} is verified.`
          : 'Not verified yet — click the link in the confirmation email.',
        verified ? 'success' : 'info',
        verified ? 'check' : 'mail',
      );
    } catch {
      toast('Could not refresh verification status.', 'danger', 'x');
    }
  };

  // Option C: DNS verification is a real, config-gated feature. When no DNS
  // provider is configured server-side we say so honestly (no fake records);
  // the working path is the email-token sender verification above.
  const checkDns = async () => {
    const status = integrationsStatus ?? (await loadIntegrationsStatus());
    if (status?.emailDns?.configured) {
      toast(`DNS verification via ${status.emailDns.provider}. Re-checking…`, 'info', 'refresh');
    } else {
      toast('DNS verification is not configured. Use email sender verification instead.', 'info');
    }
  };

  // ─── Template actions ─────────────────────────────────────────
  const updateTemplate = (slug: string, patch: Partial<Template>) => {
    setTemplates(ts => ts.map(t => t.slug === slug ? { ...t, ...patch } : t));
  };

  const saveTemplate = async (slug: string) => {
    const tpl = templates.find(t => t.slug === slug);
    const meta = templateMetaRef.current[slug];
    if (!tpl || !meta) { toast('Template saved.', 'success', 'check'); return; }
    try {
      await apiClient.patch(`${EMAIL_TEMPLATES.update(meta.templateId, meta.locale)}`, {
        subject: tpl.subject, bodyHtml: tpl.body,
      });
      updateTemplate(slug, { status: 'customised', editedAgo: 'just now', editedBy: 'You' });
      toast('Template saved.', 'success', 'check');
    } catch {
      toast('Failed to save template.', 'danger', 'x');
    }
  };

  const restoreTemplate = async (slug: string) => {
    const meta = templateMetaRef.current[slug];
    if (!meta) { toast('Restored to default.', 'success', 'refresh'); return; }
    try {
      await apiClient.post(`${EMAIL_TEMPLATES.reset(meta.templateId, meta.locale)}`);
      updateTemplate(slug, { status: 'default', editedAgo: null, editedBy: null });
      toast('Restored to default.', 'success', 'refresh');
    } catch {
      toast('Failed to restore template.', 'danger', 'x');
    }
  };

  const addTemplate = (data: Partial<Template>) => {
    const slug = data.slug || newId();
    const t: Template = {
      slug, name: data.name || '', desc: data.desc || 'Custom template.',
      status: 'customised', editedAgo: 'just now', editedBy: 'You',
      subject: data.subject || '', body: data.body || '',
    };
    setTemplates(ts => [...ts, t]);
    toast(`Template "${data.name}" created.`, 'success', 'check');
    setTimeout(() => navigate('/admin/settings/email-templates/' + slug), 100);
    return t;
  };

  const deleteTemplate = async (slug: string) => {
    const meta = templateMetaRef.current[slug];
    if (meta) {
      try { await apiClient.post(`${EMAIL_TEMPLATES.reset(meta.templateId, meta.locale)}`); } catch {}
    }
    setTemplates(ts => ts.filter(t => t.slug !== slug));
    toast('Template deleted.', 'info');
  };

  const sendTestEmail = async (slug?: string) => {
    if (slug) {
      const meta = templateMetaRef.current[slug];
      if (meta) {
        try {
          await apiClient.post(EMAIL_TEMPLATES.preview(meta.templateId, meta.locale));
          toast('Test email sent to your address.', 'success', 'send');
          return;
        } catch (e: any) {
          toast(e?.response?.data?.message || 'Failed to send test email.', 'danger', 'x');
          return;
        }
      }
    }
    toast('Test email sent.', 'success', 'send');
  };

  // ─── Contact actions ──────────────────────────────────────────
  // ─── Logout ──────────────────────────────────────────────────
  const logout = async () => {
    try { await authService.logout(); } catch {}
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('auth:expired'));
    }
  };

  // ─── Update ticket status ─────────────────────────────────────
  function mapFrontendStatus(s: string): any {
    if (s === 'resolved') return 'resolved';
    if (s === 'closed') return 'closed';
    if (s === 'pending' || s === 'on_hold') return 'pending';
    return 'open';
  }

  const updateTicketStatus = async (id: string, status: string) => {
    const apiId = getApiId(id);
    if (!apiId) return;
    try {
      await apiClient.patch(TICKETS.status(apiId), { status });
      updateTicketLocal(id, t => ({ ...t, status: mapFrontendStatus(status), _apiStatus: status }));
      toast(`Status updated to ${status.replace(/_/g, ' ')}.`, 'success', 'check');
    } catch (e) {
      toast('Failed to update status.', 'danger', 'x');
    }
  };

  // ─── Update ticket priority ───────────────────────────────────
  const updateTicketPriority = async (id: string, priority: string) => {
    const apiId = getApiId(id);
    if (!apiId) return;
    try {
      await apiClient.patch(TICKETS.update(apiId), { priority });
      updateTicketLocal(id, t => ({ ...t, priority: priority as any }));
      toast(`Priority updated to ${priority}.`, 'success', 'check');
    } catch (e) {
      toast('Failed to update priority.', 'danger', 'x');
    }
  };

  // ─── Assign ticket ────────────────────────────────────────────
  const assignTicket = async (id: string, platformUserId: string) => {
    const apiId = getApiId(id);
    if (!apiId) return;
    try {
      // Backend contract: PATCH /tickets/:id/assign { platformUserId }.
      await ticketsService.assign(apiId, platformUserId);
      const agentName = agentMap[platformUserId] || platformUserId;
      const initials = agentName.split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2);
      updateTicketLocal(id, t => ({ ...t, assignedTo: platformUserId, assignee: { name: agentName, initials, color: 'linear-gradient(135deg,#6366f1,#a855f7)' } }));
      toast(`Assigned to ${agentName}.`, 'success', 'check');
    } catch (e) {
      toast('Failed to assign ticket.', 'danger', 'x');
    }
  };

  // ─── Add tag to ticket ────────────────────────────────────────
  const addTagToTicket = async (id: string, tag: string) => {
    if (!tag.trim()) return;
    const apiId = getApiId(id);
    if (!apiId) return;
    const ticket = tickets.find(t => t.id === id);
    const newTags = [...(ticket?.tags || []), tag.trim()];
    try {
      await apiClient.patch(TICKETS.update(apiId), { tags: newTags });
      updateTicketLocal(id, t => ({ ...t, tags: newTags }));
      toast(`Tag "${tag.trim()}" added.`, 'success', 'check');
    } catch (e) {
      toast('Failed to add tag.', 'danger', 'x');
    }
  };

  // Refresh just the contacts list from the API + ticket-derived contacts.
  const loadContacts = async () => {
    try {
      const res = await contactsService.list();
      const apiContacts = ((res as any)?.data || []).map(mapApiContact);
      setTickets(ts => linkTicketCustomers(ts, apiContacts));
      setContacts(mergeContacts(apiContacts, extractCustomers(linkTicketCustomers(tickets, apiContacts))));
    } catch (e) {
      console.error('loadContacts error:', e);
    }
  };

  // Create a real contact (caller). The New Contact modal sends
  // { name, phones[], emails[], company, tags }. A legacy ticket-style payload
  // ({ subject, customerEmail }) still falls through to ticket creation.
  const addContact = async (data: any) => {
    if (data && (data.subject || data.customerEmail)) {
      await addTicket({
        subject: data.subject || `New ticket for ${data.name || data.email || 'contact'}`,
        customerEmail: data.customerEmail || data.email,
        priority: data.priority || 'medium',
        channel: 'manual',
        body: data.body,
      });
      return;
    }
    const phone = (data.phones || []).find(Boolean) || data.phone || null;
    const email = (data.emails || []).find(Boolean) || data.email || null;
    if (!data?.name || (!phone && !email)) {
      toast('Add a name and at least a phone or email.', 'warn', 'alert');
      return;
    }
    try {
      const created = await contactsService.create({
        displayName: data.name,
        phone,
        email,
        company: data.company || null,
      });
      await loadContacts();
      toast(`Contact "${created.displayName || data.name}" saved.`, 'success', 'check');
      // Optional: spin up a ticket for this contact right after creating it.
      if (data.createLead && email) {
        await addTicket({ subject: `New lead — ${data.name}`, customerEmail: email, priority: 'medium', channel: 'manual' });
      }
    } catch (e: any) {
      toast(e?.response?.data?.message || 'Failed to create contact.', 'danger', 'x');
    }
  };

  // Edit a contact — PATCH /v1/contacts/:id → CRM (source of truth). Throws so
  // the caller (dialog) can keep the form open on failure.
  const updateContact = async (id: string, patch: { displayName?: string; email?: string | null; company?: string | null }) => {
    const updated = await contactsService.update(id, patch);
    await loadContacts();
    return updated;
  };

  // Delete a contact — DELETE /v1/contacts/:id → CRM.
  const deleteContact = async (id: string) => {
    await contactsService.remove(id);
    await loadContacts();
  };

  // ─── Notification / Inbox read-state (real, persisted) ─────────
  const markAllNotificationsRead = (ids: string[] = []) => {
    persistReadInbox([...readInboxIds, ...ids]);
    AsyncStorage.setItem('notif:readAt', String(Date.now())).catch(() => {});
    toast('All notifications marked as read.', 'success', 'check');
  };
  const markInboxRead = (ids: string[]) => {
    persistReadInbox([...readInboxIds, ...(Array.isArray(ids) ? ids : [ids])]);
  };
  const markAllInboxRead = (ids: string[]) => {
    persistReadInbox([...readInboxIds, ...ids]);
    toast(`Marked ${ids.length} inbox item${ids.length === 1 ? '' : 's'} as read.`, 'success', 'check');
  };
  const refreshInbox = async () => {
    await loadAll();
    toast('Inbox refreshed.', 'info', 'refresh');
  };

  // Server-side search. Combines two API calls:
  //   • Full-text:  GET /v1/tickets?q=…  (Postgres tsquery over subject + comments)
  //   • By-number:  GET /v1/tickets/by-number/N  when the query looks like a
  //     ticket number ("3", "#3", "TKT-3") — full-text doesn't index the number.
  // Results are merged (exact number match first), deduped, and mapped the same
  // way loadAll does, enriching assignee names from the current agent map.
  const searchTickets = async (q: string): Promise<Ticket[]> => {
    const trimmed = q.trim();

    const listP = ticketsService
      .list({ q: trimmed, limit: 200 })
      .then((res: any) => {
        const raw = res?.data || res || [];
        return Array.isArray(raw) ? raw : [];
      })
      .catch(() => []);

    // Only do a by-number lookup when the whole query is a ticket-number ref.
    const numMatch = trimmed.match(/^(?:tkt[-\s]?)?#?(\d+)$/i);
    const byNumberP = numMatch
      ? ticketsService
          .getByNumber(Number(numMatch[1]))
          .then((res: any) => {
            const t = res?.data || res;
            return t && (t.id || t.number != null) ? [t] : [];
          })
          .catch(() => []) // 404 = no such ticket number; ignore
      : Promise.resolve([] as any[]);

    const [listRaw, numberRaw] = await Promise.all([listP, byNumberP]);

    // Exact number match first, then full-text hits; dedupe by ticket number.
    const seen = new Set<number | string>();
    const out: Ticket[] = [];
    for (const t of [...numberRaw, ...listRaw]) {
      const key = t.number ?? t.id;
      if (seen.has(key)) continue;
      seen.add(key);
      const ticket = mapApiTicket(t);
      if (ticket.assignee && t.assignedTo && agentMap[t.assignedTo]) {
        const dn = agentMap[t.assignedTo];
        ticket.assignee.name = dn;
        ticket.assignee.initials = dn.split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2);
      }
      out.push(ticket);
    }
    return linkTicketCustomers(out, contacts);
  };

  // Server-side contact search. Hits GET /v1/contacts?q=… (ILIKE over
  // name / email / phone / company) and maps to the customer shape the
  // Contacts table renders.
  const searchContacts = async (q: string): Promise<any[]> => {
    const res = await contactsService.list(q);
    const raw = (res as any)?.data || [];
    return (Array.isArray(raw) ? raw : []).map((c: any) => ({
      ...mapApiContact(c),
      tickets: [], openCount: 0, lastContact: null,
    }));
  };

  const copy = (text: string, label?: string) => {
    try { if (typeof navigator !== 'undefined') navigator.clipboard.writeText(text); } catch {}
    toast(`Copied ${label || text}.`, 'info', 'copy');
  };

  // ─── INTERNAL space partition: selectors + actions ───
  const spaceById = (id: string | null): ApiSpace | undefined =>
    (id ? [...spaces, ...allSpaces].find(s => s.id === id) : undefined);
  const activeSpace = spaceById(activeSpaceId);
  const isInternalSpace = (id: string | null) => spaceById(id)?.type === 'INTERNAL';

  // Default-deny is already enforced by RLS server-side; here we simply narrow
  // the loaded (already-visible) tickets to the active space — the same scoping
  // the prototype's `visibleTickets` does, but on a server-trusted dataset.
  const visibleTickets = activeSpaceId
    ? tickets.filter(t => (t as any).spaceId === activeSpaceId)
    : tickets;

  // Per-space counts the principal can actually see — drives the switcher chip
  // + the top-bar focus strip. `open` counts open + pending (awaiting) work.
  const spaceTicketStats = (spaceId: string) => {
    const inScope = tickets.filter(t => (t as any).spaceId === spaceId);
    return {
      total: inScope.filter(t => t.status !== 'closed').length,
      open: inScope.filter(t => t.status === 'open' || t.status === 'pending').length,
    };
  };

  /**
   * The assignment-only user (SKTICK-DB-001 Surface A).
   *
   * Zero accessible spaces. Before WP-1 this could not happen — every user was
   * a member of at least one space, because membership was the only route to a
   * ticket. The assignee predicate created a second kind of user, and the
   * console has to work for them without leaking the existence of anything
   * they cannot see.
   *
   * Null while loading: landing them on space chrome and then snatching it
   * away would both flicker and, briefly, name a space they must not see.
   */
  const isAssignmentOnly: boolean | null = isAssignmentOnlyUser({
    loading: isLoading, isAdmin, accessibleSpaceCount: spaces.length,
  });

  const reloadSpaces = async () => {
    try {
      const [acc, all] = await Promise.allSettled([
        spacesService.listAccessible(),
        isAdmin ? spacesService.listAll() : Promise.resolve({ data: [], count: 0 } as any),
      ]);
      if (acc.status === 'fulfilled') setSpaces((acc.value as any)?.data || []);
      if (all.status === 'fulfilled') setAllSpaces((all.value as any)?.data || []);
    } catch (e) { /* non-fatal */ }
  };

  // Switching space re-scopes every list/filter to that space. Crossing the
  // PUBLIC/INTERNAL boundary is a deliberate act — only ever via this action.
  const setActiveSpaceId = (id: string) => {
    if (id === activeSpaceId) return;
    const target = spaceById(id);
    if (!target || !spaces.some(s => s.id === id)) {
      toast("You don't have access to that space.", 'warn');
      return;
    }
    setActiveSpaceIdState(id);
    AsyncStorage.setItem(activeSpaceKey, id).catch(() => {});
    toast(`Switched to ${target.name}.`, 'info');
    navigate('/tickets');
  };

  // Revoked / deactivated mid-session: if the active space is no longer
  // accessible, bounce cleanly to the first accessible space (prefer PUBLIC).
  useEffect(() => {
    if (!activeSpaceId || spaces.length === 0) return;
    if (spaces.some(s => s.id === activeSpaceId)) return;
    const fallback = spaces.find(s => s.type === 'PUBLIC') || spaces[0];
    setActiveSpaceIdState(fallback.id);
    AsyncStorage.setItem(activeSpaceKey, fallback.id).catch(() => {});
    toast('Your access to that space changed — switched you to a space you can see.', 'warn');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaces, activeSpaceId]);

  const createSpace = async (data: { name: string; key: string; type: 'PUBLIC' | 'INTERNAL' }) => {
    try {
      const created = await spacesService.create({
        name: data.name.trim(),
        // Backend requires a lowercase alnum/dash slug; the UI shows it uppercased.
        key: data.key.trim().toLowerCase().replace(/[^a-z0-9-]/g, ''),
        type: data.type,
      });
      await reloadSpaces();
      toast(`${data.type === 'INTERNAL' ? 'Internal' : 'Public'} space "${created.name}" created.`, 'success', 'check');
      setTimeout(() => navigate('/admin/settings/spaces/' + created.id), 120);
      return created;
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 403) {
        toast('Internal spaces are not enabled for this organisation yet.', 'warn');
      } else if (status === 409) {
        toast('That space key is already in use — pick another.', 'warn');
      } else {
        toast('Failed to create space.', 'danger', 'x');
      }
      return null;
    }
  };

  const deactivateSpace = async (id: string) => {
    try {
      await spacesService.deactivate(id);
      await reloadSpaces();
      toast('Space deactivated · history retained, never deleted.', 'info', 'lock');
      return true;
    } catch (e) {
      toast('Failed to deactivate space.', 'danger', 'x');
      return false;
    }
  };

  const grantMember = async (spaceId: string, platformUserId: string, role: SpaceRole) => {
    try {
      await spacesService.grant(spaceId, platformUserId, role);
      await reloadSpaces();
      const name = agentMap[platformUserId] || platformUserId;
      toast(`Granted ${name} ${role.charAt(0) + role.slice(1).toLowerCase()} access.`, 'success', 'check');
      return true;
    } catch (e) {
      toast('Failed to grant access.', 'danger', 'x');
      return false;
    }
  };

  const revokeMember = async (spaceId: string, platformUserId: string) => {
    try {
      await spacesService.revoke(spaceId, platformUserId);
      await reloadSpaces();
      const name = agentMap[platformUserId] || platformUserId;
      toast(`Revoked ${name}'s access — effective immediately.`, 'warn', 'lock');
      return true;
    } catch (e) {
      toast('Failed to revoke access.', 'danger', 'x');
      return false;
    }
  };

  const value: AppContextType = {
    tickets, channel, templates, contacts, toasts, route,
    // partition
    spaces, allSpaces, activeSpaceId, activeSpace,
    spaceById, isInternalSpace, visibleTickets, spaceTicketStats, isAssignmentOnly,
    setActiveSpaceId, reloadSpaces, createSpace, deactivateSpace, grantMember, revokeMember,
    isLoading, agentMap,
    automations, orgSettings, apiTokens, webhooks,
    integrationsStatus, readInboxIds,
    postComment, editComment, deleteComment, resolveTicket, deleteTicket, toggleDeliveryExpanded,
    updateTicketStatus, updateTicketPriority, assignTicket, addTagToTicket,
    updateChannel, sendVerification, confirmVerification, checkDns, saveChannel,
    updateTemplate, addTemplate, deleteTemplate, saveTemplate, restoreTemplate, sendTestEmail,
    addContact, updateContact, deleteContact, loadContacts, addTicket, toast, copy,
    markAllNotificationsRead, markInboxRead, markAllInboxRead, refreshInbox, searchTickets, searchContacts,
    navigate, loadTicketComments, reloadTicket,
    loadAutomations, toggleAutomation, deleteAutomation, createAutomation, updateAutomation,
    saveOrgSettings,
    loadApiTokens, createApiToken, revokeApiToken, updateApiToken,
    loadWebhooks, createWebhook, updateWebhook, deleteWebhook, testWebhook,
    loadSigningSecret, rotateSigningSecret, loadIntegrationsStatus,
    logout,
  };

  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}
